import React from "react";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import PropTypes from "prop-types";
import { validateEmail } from "../../config/utils";

export const RELATIONSHIP_OWNER = "Owner";
export const RELATIONSHIP_REPRESENTATIVE = "Representative";
export const DEFAULT_OWNER_DESIGNATION = "Owner / Proprietor";
export const OWNER_ROLES = ["Owner / Proprietor", "Partner", "Director", "CEO / Managing Director", "Other"];
export const NOT_CAPTURED = "Not captured";

// Order used to focus the first invalid field.
export const OWNERSHIP_FIELD_ORDER = [
  "companyName",
  "designation",
  "relationshipType",
  "ownerFullName",
  "ownerRole",
  "ownerDirectPhone",
  "ownerWhatsAppNumber",
  "ownerBusinessEmail",
  "isAuthorizationConfirmed",
];

const digitsOnly = (value) => (value || "").replaceAll(/\D/g, "");
const lastTenDigits = (value) => digitsOnly(value).slice(-10);

export const buildOwnershipState = (src = {}) => {
  const ownerDirectPhone = src?.ownerDirectPhone || "";
  const ownerWhatsAppNumber = src?.ownerWhatsAppNumber || "";
  return {
    companyName: src?.companyName || "",
    relationshipType: src?.relationshipType || "",
    designation: src?.designation || "",
    ownerFullName: src?.ownerFullName || "",
    ownerRole: src?.ownerRole || "",
    ownerDirectPhone,
    ownerWhatsAppNumber,
    whatsAppSameAsPhone: Boolean(ownerDirectPhone) && ownerDirectPhone === ownerWhatsAppNumber,
    ownerBusinessEmail: src?.ownerBusinessEmail || "",
    isAuthorizationConfirmed: Boolean(src?.isAuthorizationConfirmed),
    authorizationConfirmedDate: src?.authorizationConfirmedDate || null,
    isOwnerContactVerified: Boolean(src?.isOwnerContactVerified),
  };
};

export const buildOwnershipPayload = (form, data) => {
  const isRepresentative = form.relationshipType === RELATIONSHIP_REPRESENTATIVE;
  return {
    onboardingId: data?.Id || data?.onboardingId || 0,
    companyName: (form.companyName || "").trim(),
    relationshipType: form.relationshipType,
    designation: (form.designation || "").trim(),
    ownerFullName: isRepresentative ? (form.ownerFullName || "").trim() : "",
    ownerRole: isRepresentative ? form.ownerRole : "",
    ownerDirectPhone: isRepresentative ? (form.ownerDirectPhone || "").trim() : "",
    ownerWhatsAppNumber: isRepresentative ? (form.ownerWhatsAppNumber || "").trim() : "",
    ownerBusinessEmail: isRepresentative ? (form.ownerBusinessEmail || "").trim() : "",
    isAuthorizationConfirmed: isRepresentative ? Boolean(form.isAuthorizationConfirmed) : null,
  };
};

const validateOwnerPhones = (form, accountPhone) => {
  const errors = {};
  const phoneDigits = digitsOnly(form.ownerDirectPhone);
  if (!phoneDigits) {
    errors.ownerDirectPhone = "Owner direct phone is required";
  } else if (phoneDigits.length < 10 || phoneDigits.length > 15) {
    errors.ownerDirectPhone = "Enter a valid phone number";
  } else if (lastTenDigits(form.ownerDirectPhone) === lastTenDigits(accountPhone)) {
    errors.ownerDirectPhone = "Owner phone must be different from your own phone";
  }

  const whatsAppDigits = digitsOnly(form.ownerWhatsAppNumber);
  if (whatsAppDigits && (whatsAppDigits.length < 10 || whatsAppDigits.length > 15)) {
    errors.ownerWhatsAppNumber = "Enter a valid WhatsApp number";
  }
  return errors;
};

const validateOwnerDetails = (form, accountPhone, accountEmail) => {
  const errors = validateOwnerPhones(form, accountPhone);
  if (!form.ownerFullName.trim()) errors.ownerFullName = "Owner full name is required";
  if (!form.ownerRole) errors.ownerRole = "Select the owner's role";

  const ownerEmail = form.ownerBusinessEmail.trim();
  if (!ownerEmail) {
    errors.ownerBusinessEmail = "Owner business email is required";
  } else if (!validateEmail(ownerEmail)) {
    errors.ownerBusinessEmail = "Enter a valid email address";
  } else if (ownerEmail.toLowerCase() === (accountEmail || "").trim().toLowerCase()) {
    errors.ownerBusinessEmail = "Owner email must be different from your own email";
  }

  if (!form.isAuthorizationConfirmed) errors.isAuthorizationConfirmed = "Please confirm the owner's authorization";
  return errors;
};

export const validateOwnership = (form, accountPhone, accountEmail) => {
  const errors = {};
  if (!form.companyName.trim()) errors.companyName = "Company name is required";
  if (!form.designation.trim()) errors.designation = "Designation is required";
  if (!form.relationshipType) {
    errors.relationshipType = "Select your relationship to this company";
  } else if (form.relationshipType === RELATIONSHIP_REPRESENTATIVE) {
    Object.assign(errors, validateOwnerDetails(form, accountPhone, accountEmail));
  }
  return errors;
};

const formatConfirmedDate = (value) => {
  if (!value) return "";
  return moment.utc(value).local().format("MM-DD-YYYY | h:mm A");
};

const ReadOnlyItem = ({ label, value }) => (
  <div className="mb-3">
    <strong>{label}:</strong> <span className="text-muted">{value || NOT_CAPTURED}</span>
  </div>
);

ReadOnlyItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

export const CompanyOwnershipReadOnly = ({ form, canMarkVerified = false, verifying = false, onToggleVerified }) => {
  const isRepresentative = form.relationshipType === RELATIONSHIP_REPRESENTATIVE;
  const signedUpAs = {
    [RELATIONSHIP_OWNER]: "Owner",
    [RELATIONSHIP_REPRESENTATIVE]: "Representative",
  }[form.relationshipType];

  let authorizationText = "No";
  if (form.isAuthorizationConfirmed) {
    const confirmedDate = form.authorizationConfirmedDate
      ? ` - ${formatConfirmedDate(form.authorizationConfirmedDate)}`
      : "";
    authorizationText = `Yes${confirmedDate}`;
  }

  return (
    <>
      <Row className="my-4">
        <Col md={6} lg={6}>
          <ReadOnlyItem label="Company" value={form.companyName} />
          <ReadOnlyItem label="Signed up as" value={signedUpAs} />
          {isRepresentative && <ReadOnlyItem label="Authorization confirmed" value={authorizationText} />}
        </Col>
        <Col md={6} lg={6}>
          <ReadOnlyItem label="Designation" value={form.designation} />
          {isRepresentative && (
            <>
              <ReadOnlyItem label="Owner contact verified" value={form.isOwnerContactVerified ? "Verified" : "Not verified"} />
              {canMarkVerified && (
                <button type="button" className="btn btn-sm btn-outline-primary mb-3" disabled={verifying} onClick={onToggleVerified}>
                  {form.isOwnerContactVerified ? "Remove verification" : "Mark verified"}
                </button>
              )}
            </>
          )}
        </Col>
      </Row>
      {isRepresentative && (
        <Row>
          <Col md={6} lg={6}>
            <ReadOnlyItem label="Owner full name" value={form.ownerFullName} />
            <ReadOnlyItem label="Owner direct phone" value={form.ownerDirectPhone} />
            <ReadOnlyItem label="Owner business email" value={form.ownerBusinessEmail} />
          </Col>
          <Col md={6} lg={6}>
            <ReadOnlyItem label="Owner role" value={form.ownerRole} />
            <ReadOnlyItem label="Owner WhatsApp number" value={form.ownerWhatsAppNumber} />
          </Col>
        </Row>
      )}
    </>
  );
};

CompanyOwnershipReadOnly.propTypes = {
  form: PropTypes.object.isRequired,
  canMarkVerified: PropTypes.bool,
  verifying: PropTypes.bool,
  onToggleVerified: PropTypes.func,
};

const FieldError = ({ message }) => (message ? <p className="error dialogError">{message}</p> : null);

FieldError.propTypes = {
  message: PropTypes.string,
};

const RelationshipCard = ({ value, title, description, selected, onSelect }) => (
  <label // NOSONAR - the text comes from the title and description props rendered below
    className={`co-radio-card ${selected ? "selected" : ""}`}
    htmlFor={`co-relationship-${value}`}
  >
    <input
      id={`co-relationship-${value}`}
      type="radio"
      name="relationshipType"
      value={value}
      checked={selected}
      onChange={() => onSelect(value)}
    />
    <span className="co-radio-card-text">
      <span className="co-radio-card-title">{title}</span>
      <span className="co-radio-card-desc">{description}</span>
    </span>
  </label>
);

RelationshipCard.propTypes = {
  value: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const CompanyOwnershipEditor = ({ form, errors, onChange, accountDetails }) => {
  const isRepresentative = form.relationshipType === RELATIONSHIP_REPRESENTATIVE;

  const renderInput = (name, label, { required = false, type = "text", placeholder = "", disabled = false } = {}) => (
    <div className="mb-3">
      <label htmlFor={`co-${name}`}>
        {label}{required && <span className="error"> *</span>}
      </label>
      <input
        id={`co-${name}`}
        type={type}
        name={name}
        value={form[name] || ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(name, e.target.value)}
        className="form-control"
      />
      <FieldError message={errors[name]} />
    </div>
  );

  return (
    <>
      <Row>
        <Col md={6} lg={6}>
          {renderInput("companyName", "Company name", { required: true, placeholder: "Registered business name" })}
        </Col>
        <Col md={6} lg={6}>
          {renderInput("designation", "Your designation", { required: true })}
        </Col>
      </Row>

      <fieldset className="mb-3 co-fieldset">
        <legend className="co-legend">
          What is your relationship to this company?<span className="error"> *</span>
        </legend>
        <Row>
          <Col md={6} lg={6}>
            <RelationshipCard
              value={RELATIONSHIP_OWNER}
              title="I am the owner / proprietor"
              description="I am legally and financially responsible for this company."
              selected={form.relationshipType === RELATIONSHIP_OWNER}
              onSelect={(value) => onChange("relationshipType", value)}
            />
          </Col>
          <Col md={6} lg={6}>
            <RelationshipCard
              value={RELATIONSHIP_REPRESENTATIVE}
              title="I am an authorized representative"
              description="I am signing up on behalf of the owner (employee, manager, agent)."
              selected={isRepresentative}
              onSelect={(value) => onChange("relationshipType", value)}
            />
          </Col>
        </Row>
        <FieldError message={errors.relationshipType} />
      </fieldset>

      {isRepresentative && (
        <>
          <h6 className="fw-bold mb-1">Owner / proprietor details</h6>
          <p className="text-muted small">We contact this person only for account and billing matters if we cannot reach you.</p>
          <Row>
            <Col md={6} lg={6}>
              {renderInput("ownerFullName", "Owner full name", { required: true })}
            </Col>
            <Col md={6} lg={6}>
              <div className="mb-3">
                <label htmlFor="co-ownerRole">
                  Owner role<span className="error"> *</span>
                </label>
                <select
                  id="co-ownerRole"
                  name="ownerRole"
                  value={form.ownerRole}
                  onChange={(e) => onChange("ownerRole", e.target.value)}
                  className="form-control"
                >
                  <option value="">Select</option>
                  {OWNER_ROLES.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <FieldError message={errors.ownerRole} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6} lg={6}>
              {renderInput("ownerDirectPhone", "Owner direct phone", { required: true, placeholder: "+1" })}
            </Col>
            <Col md={6} lg={6}>
              {renderInput("ownerWhatsAppNumber", "Owner WhatsApp number", { placeholder: "+1", disabled: form.whatsAppSameAsPhone })}
              <div className="form-check mb-3">
                <input
                  id="co-whatsAppSameAsPhone"
                  type="checkbox"
                  name="whatsAppSameAsPhone"
                  className="form-check-input"
                  checked={form.whatsAppSameAsPhone}
                  onChange={(e) => onChange("whatsAppSameAsPhone", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="co-whatsAppSameAsPhone">Same as direct phone</label>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6} lg={6}>
              {renderInput("ownerBusinessEmail", "Owner business email", { required: true, type: "email", placeholder: "name@company.com" })}
            </Col>
          </Row>
          <div className="co-warning" role="note">
            {`The owner's phone and email must be different from your own (${accountDetails?.email || ""}, ${accountDetails?.phoneNumber || ""}).`}
          </div>
          <div className="form-check mb-3">
            <input
              id="co-isAuthorizationConfirmed"
              type="checkbox"
              name="isAuthorizationConfirmed"
              className="form-check-input"
              checked={form.isAuthorizationConfirmed}
              onChange={(e) => onChange("isAuthorizationConfirmed", e.target.checked)}
            />
            <label className="form-check-label" htmlFor="co-isAuthorizationConfirmed">
              I confirm the owner has authorized me to create and operate this account, and agrees to be contacted about it.{" "}
              <span className="error">*</span>
            </label>
            <FieldError message={errors.isAuthorizationConfirmed} />
          </div>
        </>
      )}
    </>
  );
};

CompanyOwnershipEditor.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  accountDetails: PropTypes.object,
};

export default function CompanyOwnershipSection({ form, errors = {}, editable = false, onChange, accountDetails, canMarkVerified, verifying, onToggleVerified }) {
  return (
    <section className="co-section" aria-labelledby="co-section-title">
      <div className="co-section-head">
        <h6 id="co-section-title" className="fw-bold mb-0 font-20">Company &amp; Ownership</h6>
      </div>
      <hr className="my-3" />
      {editable
        ? <CompanyOwnershipEditor form={form} errors={errors} onChange={onChange} accountDetails={accountDetails} />
        : <CompanyOwnershipReadOnly form={form} canMarkVerified={canMarkVerified} verifying={verifying} onToggleVerified={onToggleVerified} />}
    </section>
  );
}

CompanyOwnershipSection.propTypes = {
  form: PropTypes.object.isRequired,
  errors: PropTypes.object,
  editable: PropTypes.bool,
  onChange: PropTypes.func,
  accountDetails: PropTypes.object,
  canMarkVerified: PropTypes.bool,
  verifying: PropTypes.bool,
  onToggleVerified: PropTypes.func,
};
